import { UPCOMING_SESSIONS_LIMIT } from '../../../../constants/app-constants';
import { Booking } from '../../../../core/models/booking';
import backendHttpClient from '../../../http/axios/axios-http-client';
import { MyAppointmentsAsClientDTO } from './dto/my-appointments-as-client.dto';
import { MyAppointmentsAsHealerDTO } from './dto/my-appointments-as-healer.dto';
import { RescheduleBookingAsClientDTO } from './dto/reschedule-booking-as-client-dto';
import { ScheduleBookingAsClientDTO } from './dto/schedule-booking-as-client-dto';
import { ScheduleBookingAsHealerDTO } from './dto/schedule-booking-as-healer-dto';
import { UpcomingSessionsForHealerDTO } from './dto/upcoming-sessions-dto';
import { UpcomingSessionsForClientDTO } from './dto/upcoming-sessions-for-client-dto';

const PUBLIC_BOOKING_ENDPOINT = '/public/booking';
const SECURED_BOOKING_ENDPOINT = '/booking';

export interface BookingGateway {
  getById(bookingId: string): Promise<Booking>;
  getUpcomingBookingsForClient(userProfileId: string, limit?: number): Promise<UpcomingSessionsForClientDTO>;
  getUpcomingBookingForHealer(limit?: number): Promise<UpcomingSessionsForHealerDTO>;
  startSession(bookingId: string, soundtrack?: string, soundtrackVolume?: number): Promise<void>;
  finishSession(bookingId: string): Promise<void>;
  startMeeting(bookingId: string): Promise<void>;
  scheduleBookingAsHealer(body: ScheduleBookingAsHealerDTO): Promise<Booking>;
  scheduleBookingAsClient(body: ScheduleBookingAsClientDTO): Promise<Booking>;
  markAsHealloCompleted(bookingId: string): Promise<void>;
  cancelBookingAsHealer(bookingId: string): Promise<void>;
  cancelBookingAsClient(bookingId: string): Promise<void>;
  rescheduleAsClient(body: RescheduleBookingAsClientDTO): Promise<Booking>;
  getMyBookedAppointmentsAsClient(clientUserProfileId: string): Promise<MyAppointmentsAsClientDTO>;
  getMyBookedAppointmentsAsHealer(): Promise<MyAppointmentsAsHealerDTO>;
  getIfHealerAndClientHaveUpcomingSessionsScheduled(clientUserProfileId: string, healerUserProfileId: string): Promise<boolean>;
  getEligibilityForReschedule(bookingId: string): Promise<boolean>;
  getLastFinishedBooking(clientUserProfileId: string): Promise<Booking | null>;
}

export class BookingGateway implements BookingGateway {
  async getById(bookingId: string): Promise<Booking> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${bookingId}`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });

    return response.data;
  }

  async getUpcomingBookingsForClient(userProfileId: string, limit = UPCOMING_SESSIONS_LIMIT): Promise<UpcomingSessionsForClientDTO> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${userProfileId}/upcoming?limit=${limit}`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });

    return response.data;
  }

  async getUpcomingBookingForHealer(limit = UPCOMING_SESSIONS_LIMIT): Promise<UpcomingSessionsForHealerDTO> {
    const endpoint = `${SECURED_BOOKING_ENDPOINT}/upcoming?limit=${limit}`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });
    return response.data;
  }

  async startSession(bookingId: string, soundtrack?: string | null, soundtrackVolume?: number | null): Promise<void> {
    const endpoint = `${SECURED_BOOKING_ENDPOINT}/${bookingId}/start-session`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'POST',
      data: {
        soundtrack: soundtrack == null ? null : soundtrack,
        volume: soundtrackVolume == null ? null : soundtrackVolume,
      },
    });
    return response.data;
  }

  async scheduleBookingAsHealer(body: ScheduleBookingAsHealerDTO): Promise<Booking> {
    const endpoint = `${SECURED_BOOKING_ENDPOINT}/schedule`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'POST',
      data: body,
    });
    return response.data;
  }
  async scheduleBookingAsClient(body: ScheduleBookingAsClientDTO): Promise<Booking> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/schedule`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'POST',
      data: body,
    });
    return response.data;
  }

  async startMeeting(bookingId: string): Promise<void> {
    const endpoint = `${SECURED_BOOKING_ENDPOINT}/${bookingId}/meeting-started`;

    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'POST',
    });
    return response.data;
  }

  async finishSession(bookingId: string): Promise<void> {
    const endpoint = `${SECURED_BOOKING_ENDPOINT}/${bookingId}/finish-session`;

    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'POST',
    });

    return response.data;
  }

  async markAsHealloCompleted(bookingId: string): Promise<void> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${bookingId}/heallo-completed`;

    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'POST',
    });
    return response.data;
  }

  async cancelBookingAsHealer(bookingId: string): Promise<void> {
    const endpoint = `${SECURED_BOOKING_ENDPOINT}/${bookingId}/cancel`;

    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'PUT',
    });
    return response.data;
  }

  async cancelBookingAsClient(bookingId: string): Promise<void> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${bookingId}/cancel`;

    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'PUT',
    });
    return response.data;
  }

  async rescheduleAsClient(body: RescheduleBookingAsClientDTO): Promise<Booking> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/reschedule`;

    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'POST',
      data: body,
    });
    return response.data;
  }

  async getMyBookedAppointmentsAsClient(clientUserProfileId: string): Promise<MyAppointmentsAsClientDTO> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${clientUserProfileId}/my`;

    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });
    return response.data;
  }

  async getMyBookedAppointmentsAsHealer(): Promise<MyAppointmentsAsHealerDTO> {
    const endpoint = `${SECURED_BOOKING_ENDPOINT}/my`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });

    return response.data;
  }

  async getIfHealerAndClientHaveUpcomingSessionsScheduled(clientUserProfileId: string, healerUserProfileId: string): Promise<boolean> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${clientUserProfileId}/${healerUserProfileId}/booking-scheduled-in-future`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });

    return response.data;
  }

  async getEligibilityForReschedule(bookingId: string): Promise<boolean> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${bookingId}/is-eligible-for-reschedule`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });

    return response.data;
  }

  async getLastFinishedBooking(clientUserProfileId: string): Promise<Booking | null> {
    const endpoint = `${PUBLIC_BOOKING_ENDPOINT}/${clientUserProfileId}/last-finished-booking`;
    const response = await backendHttpClient.request({
      url: endpoint,
      method: 'GET',
    });

    return response.data;
  }
}
